<template>
  <div class="faq-additional-access">
    <BackstagePopoverPanel>
      <template #trigger>
        <BackstageIcon class="faq-question-icon" name="question-mark" data-cy="question-mark-icon" />
      </template>
      <template #title> FAQs </template>
      <template #body>
        <div class="faq-container" data-cy="faq-container">
          <div class="header-faq-menu" data-cy="header-faq-menu">
            <ul v-if="faqSections.length">
              <li v-for="section in faqSections" :key="section.id">
                <button class="header-faq-menu-item" variant="secondary" v-on:click="goTo(section.id)">
                  {{ section.title }}
                </button>
              </li>
            </ul>
            <div v-else class="loader"></div>
          </div>
        </div>
      </template>
    </BackstagePopoverPanel>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useLocale } from '../../composables/locale';
import { FaqsService } from '../../services/faqs.service';
import { globalConstants } from '@/constants/global.constants';
import type { FaqSectionModel } from '@/models/faq-section.model';
import { useRouter } from 'vue-router';
import { RouteNames } from '@/router';
import BackstagePopoverPanel from '@/components/common/BackstagePopoverPanel.vue';

const { userLanguage } = useLocale();
const faqsService = new FaqsService();
const rawSections = ref<FaqSectionModel[]>([]);

const faqSections = computed(() =>
  rawSections.value.map((sectionModel) => {
    const section = {
      id: sectionModel.externalId,
      title: sectionModel.title[userLanguage.value] || sectionModel.title[globalConstants.fallBackLocale],
    };
    return section;
  }),
);

const router = useRouter();

const goTo = (sectionId: string) => {
  router.push({ name: RouteNames.FaqsSection, params: { id: sectionId } });
};

const fetchFaqSections = async () => {
  const result = await faqsService.getFaqSections();
  if (result.isValid() && result.model) {
    rawSections.value = result.model;
  }
};

onMounted(() => {
  fetchFaqSections();
});
</script>

<style scoped lang="scss">
.faq-question-icon {
  width: 2.625rem;
  height: 4rem;
}

ul {
  padding: 1px;
  padding-top: 0px;
  margin: 0;
  list-style: none;

  .header-faq-menu-item {
    border: none;
    width: 100%;
    padding: 0.625rem;
    border-bottom: 1px solid;
    border-bottom-color: var(--bg-remark-selected-color);
    background-color: var(--primary-bg-color);
    color: var(--primary-text-color);
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 400;
    text-align: left;
    &.active,
    &:hover {
      color: var(--primary-text-color);
      background-color: var(--bg-remark-selected-color);

      @include breakpoint(medium) {
        color: var(--primary-text-color);
      }
    }
  }

  li:first-child .header-faq-menu-item {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }
  li:last-child .header-faq-menu-item {
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }
}
</style>
